import { useState } from "react";
import { z } from "zod";
import {
  Box,
  DataTable,
  DataTableEmpty,
  Icon,
  MenuItem,
  Text,
  type WidgetModule,
  type WidgetRenderContext,
} from "@salestrak/ui";
import {
  useIssueMediaColumns,
  useIssueMediaGroups,
  useIssueMediaRows,
} from "#app/components/issue-view/issue-media-columns";
import { MediaLightbox } from "#app/components/media/media-lightbox";
import { useMediaDownloads } from "#app/lib/hooks/use-media-downloads";
import { useIssueWidgetContext } from "#app/widgets/issue/issue-widget-context";

export const issueAttachmentsConfigSchema = z.looseObject({ limit: z.number().default(5) });
export type IssueAttachmentsConfig = z.infer<typeof issueAttachmentsConfigSchema>;

function IssueAttachmentsRender({ config }: WidgetRenderContext<IssueAttachmentsConfig>) {
  const ctx = useIssueWidgetContext();
  const items = ctx.mediaItems ?? [];
  const { rows, lightboxMedia } = useIssueMediaRows({ items });
  const visibleRows = rows.slice(0, config.limit);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const { downloadPhoto } = useMediaDownloads(ctx.issue?.name);
  const columns = useIssueMediaColumns({
    onOpenLightbox: setLightboxIndex,
    onNavigateFallback: () => ctx.onNavigateTab?.("media"),
    tileSize: 24,
  });
  const groups = useIssueMediaGroups(visibleRows, ctx.issue?.issueId ?? "");

  if (items.length === 0) {
    return (
      <Box paddingY="sm">
        <Text size="sm" variant="muted">No attachments yet</Text>
      </Box>
    );
  }

  return (
    <Box gap="sm">
      <Box direction="column" border rounded="sm" overflow="hidden">
        <DataTable
          columns={columns}
          groups={groups}
          getRowId={(row, index) => `${row.photoId}-${index}`}
          density="compact"
          scrollMode="fit"
          fillColumnId="location"
          paddingX="xs"
          onRowClick={(row) => setLightboxIndex(row.original.index)}
          renderRowActions={(row) => (
            <MenuItem onClick={() => downloadPhoto(row.photoId, row.index + 1, row.item.fileName)}>
              <Icon name="download" size="xs" />
              <Text size="sm">Download</Text>
            </MenuItem>
          )}
          emptyState={<DataTableEmpty message="No attachments yet" />}
        />
      </Box>
      {items.length > visibleRows.length ? (
        <Box direction="row" align="center" gap="sm" paddingLeft="sm">
          <Text size="sm" variant="muted">+{items.length - visibleRows.length} more</Text>
        </Box>
      ) : null}
      <MediaLightbox
        media={lightboxMedia}
        open={lightboxIndex !== null}
        onOpenChange={(open) => {
          if (!open) setLightboxIndex(null);
        }}
        initialIndex={lightboxIndex ?? 0}
        title="Attachments"
        downloadPrefix={ctx.issue?.name}
      />
    </Box>
  );
}

function IssueAttachmentsTitleAdornment() {
  const ctx = useIssueWidgetContext();
  const count = ctx.mediaItems?.length ?? 0;

  if (count === 0) return null;

  return (
    <Text size="sm" variant="muted" tabular>
      {count}
    </Text>
  );
}

export const issueAttachmentsDef: WidgetModule<IssueAttachmentsConfig> = {
  type: "issue-attachments",
  title: "Attachments",
  icon: "paperclip",
  span: { minW: 3, minH: 2, maxW: 12, maxH: 12, defaultW: 12, defaultH: 3 },
  configSchema: issueAttachmentsConfigSchema,
  titleAdornment: IssueAttachmentsTitleAdornment,
  render: IssueAttachmentsRender,
};
